import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, CheckCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { adminAPI } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import autoPrintController from '../../services/printer/autoPrintController';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationCenter = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [previousCount, setPreviousCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [seenOrders, setSeenOrders] = useState(() => {
    const saved = localStorage.getItem('admin_seen_orders');
    return saved ? JSON.parse(saved) : [];
  });

  // Web Audio API references — synthesised tone, no external asset needed.
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  // Number of times we've nagged (looped sound) for the current unread batch.
  const nagCountRef = useRef(0);
  // Cache of seen orders for use inside the polling callback closure.
  const seenOrdersRef = useRef(seenOrders);
  useEffect(() => { seenOrdersRef.current = seenOrders; }, [seenOrders]);

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      audioCtxRef.current = new AC();
    }
    return audioCtxRef.current;
  }, []);

  const playNotificationSound = useCallback(() => {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // Loud, attention-grabbing chime. We layer a sine + square so it cuts
    // through ambient kitchen noise without sounding distorted. Each beep
    // also routes through a gentle limiter (DynamicsCompressor) to keep the
    // signal hot but click-free.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -8;
    limiter.knee.value = 6;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.1;
    limiter.connect(ctx.destination);

    const playBeep = (freq, startOffset, duration = 0.45) => {
      const start = now + startOffset;
      // Layer 1: warm sine
      const sine = ctx.createOscillator();
      sine.type = 'sine';
      sine.frequency.value = freq;
      // Layer 2: bright square one octave down for body
      const square = ctx.createOscillator();
      square.type = 'square';
      square.frequency.value = freq / 2;

      const gain = ctx.createGain();
      sine.connect(gain);
      square.connect(gain);
      gain.connect(limiter);

      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.95, start + 0.015);
      gain.gain.setValueAtTime(0.95, start + duration - 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

      sine.start(start);
      square.start(start);
      sine.stop(start + duration + 0.02);
      square.stop(start + duration + 0.02);
    };

    // Three-note urgent alert (A5 → E5 → A5 → high A6) — loud enough to be
    // heard across a busy kitchen.
    playBeep(880, 0);
    playBeep(659.25, 0.22);
    playBeep(880, 0.45);
    playBeep(1318.5, 0.7, 0.55);
  }, [ensureAudioContext]);

  // Unlock audio on first user interaction (browser autoplay policy).
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      const ctx = ensureAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      audioUnlockedRef.current = true;
    };
    window.addEventListener('click', unlock);
    window.addEventListener('keydown', unlock);
    window.addEventListener('touchstart', unlock);
    return () => {
      window.removeEventListener('click', unlock);
      window.removeEventListener('keydown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [ensureAudioContext]);

  /**
   * Request browser Notification permission once per session.
   * Fires after the first user interaction so the prompt is allowed.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!('Notification' in window)) return undefined;
    if (Notification.permission !== 'default') return undefined;

    const askPermission = () => {
      Notification.requestPermission().catch(() => {});
      window.removeEventListener('click', askPermission);
      window.removeEventListener('keydown', askPermission);
    };
    window.addEventListener('click', askPermission, { once: true });
    window.addEventListener('keydown', askPermission, { once: true });
    return () => {
      window.removeEventListener('click', askPermission);
      window.removeEventListener('keydown', askPermission);
    };
  }, []);

  /**
   * Show a browser-level Push Notification (banner + native sound) for a new order.
   * Works even when the admin tab is in the background.
   */
  const showBrowserNotification = useCallback((order) => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    try {
      const title = order.payment_method === 'coc'
        ? 'New COC order — collection'
        : order.payment_method === 'cod'
          ? 'New COD order — needs prep'
          : 'New paid order';
      const orderNum = order.daily_order_number
        ? `#${String(order.daily_order_number).padStart(3, '0')}`
        : `#${String(order.id || '').slice(-6).toUpperCase()}`;
      const body = `${orderNum} — ${order.customer_name || 'Customer'} — £${Number(order.total_amount || 0).toFixed(2)}`;
      const n = new Notification(title, { body, tag: order.id, requireInteraction: false });
      n.onclick = () => {
        window.focus();
        navigate('/admin/orders');
        n.close();
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Browser notification failed:', e?.message || e);
    }
  }, [navigate]);

  /**
   * Boot the unified auto-print controller once. The controller itself
   * gates on printer mode + Android bridge presence, so mounting it here
   * (on every admin page) is safe and idempotent.
   */
  useEffect(() => {
    autoPrintController.start();
    return () => autoPrintController.stop();
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await adminAPI.getNewOrdersCount();
      const { recent_orders } = response.data;

      // Filter out orders that have been seen
      const currentSeen = seenOrdersRef.current;
      const unseenOrders = recent_orders.filter(order => !currentSeen.includes(order.id));
      const unseenCount = unseenOrders.length;

      const isFreshArrival = isInitialized && unseenCount > previousCount;

      if (isFreshArrival) {
        // 1) Sound: reset nag counter so we start a fresh chime cycle.
        nagCountRef.current = 1;
        playNotificationSound();

        // 2) Browser Push Notification for each NEW unseen order (cap at 3).
        const justArrived = unseenOrders.slice(0, 3);
        justArrived.forEach(showBrowserNotification);

        // 3) Auto-printing is handled by autoPrintController — a dedicated
        //    5s poller against /api/admin/orders/print-queue with atomic
        //    per-order locks. Kept out of this notification path so a
        //    slow print never blocks the sound/notification alert.
      } else if (unseenCount > 0 && isInitialized) {
        // Keep ringing every poll cycle (every 10s) until the admin
        // acknowledges by opening the bell / clicking an order /
        // marking-all-read. This is by design — orders MUST be accepted.
        nagCountRef.current += 1;
        playNotificationSound();
      } else if (unseenCount === 0) {
        nagCountRef.current = 0;
      }

      setPreviousCount(unseenCount);
      setNewOrdersCount(unseenCount);
      setRecentOrders(recent_orders);

      if (!isInitialized) {
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [previousCount, isInitialized, playNotificationSound, showBrowserNotification]);

  useEffect(() => {
    // Initial fetch
    fetchNotifications();

    // Poll every 10 seconds
    const interval = setInterval(fetchNotifications, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  const handleMarkAllAsRead = () => {
    const allOrderIds = recentOrders.map(order => order.id);
    const updatedSeenOrders = [...new Set([...seenOrders, ...allOrderIds])];
    setSeenOrders(updatedSeenOrders);
    localStorage.setItem('admin_seen_orders', JSON.stringify(updatedSeenOrders));
    setNewOrdersCount(0);
    nagCountRef.current = 0;
  };

  const handleOrderClick = (orderId) => {
    // Mark this order as seen
    if (!seenOrders.includes(orderId)) {
      const updatedSeenOrders = [...seenOrders, orderId];
      setSeenOrders(updatedSeenOrders);
      localStorage.setItem('admin_seen_orders', JSON.stringify(updatedSeenOrders));
    }
    nagCountRef.current = 0;
    setIsOpen(false);
    navigate('/admin/orders');
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const isOrderSeen = (orderId) => {
    return seenOrders.includes(orderId);
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {newOrdersCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold"
            data-testid="notification-badge"
          >
            {newOrdersCount}
          </motion.span>
        )}
      </Button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50"
              data-testid="notification-panel"
            >
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-lg">Notifications</h3>
                  {newOrdersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleMarkAllAsRead}
                      data-testid="mark-all-read-button"
                    >
                      <CheckCheck className="h-4 w-4 mr-1" />
                      Mark all read
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {newOrdersCount} new order{newOrdersCount !== 1 ? 's' : ''} need attention
                </p>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {recentOrders.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>No recent orders</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {recentOrders.map((order) => {
                      const isSeen = isOrderSeen(order.id);
                      return (
                        <div
                          key={order.id}
                          className={`p-4 hover:bg-secondary/50 cursor-pointer transition-colors ${
                            !isSeen ? 'bg-blue-50/50' : ''
                          }`}
                          onClick={() => handleOrderClick(order.id)}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">
                                {order.customer_name}
                              </span>
                              {!isSeen && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full" />
                              )}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              order.order_status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-700'
                                : order.order_status === 'confirmed'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {order.order_status}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mb-1">
                            {order.items.length} item{order.items.length > 1 ? 's' : ''} • £{order.total_amount.toFixed(2)}
                          </p>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted-foreground capitalize">
                              {order.order_type}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatTime(order.created_at)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-3 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/admin/orders');
                  }}
                >
                  View All Orders
                </Button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};